import { computed, inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { useCreateDirectoryMutation } from '../../data/useCreateDirectoryMutation';
import { useUploadFileMutation } from '../../data/useUploadFileMutation';
import type { FileManagerMountsState } from '../core/useFileManagerMounts';
import type { FileManagerNavigationState } from '../core/useFileManagerNavigation';
import { notifyUploadBatchResult, notifyUploadBusy, uploadFolderTree, type UploadItemStatus } from './uploadFolderTree';
import { flattenFileList, type FlattenedUploadEntry } from './uploadTreeFlattening';

export interface UploadProgressItem {
  relativePath: string;
  status: UploadItemStatus;
}

export type FileManagerUploadState = {
  fileInputRef: Ref<HTMLInputElement | undefined>;
  folderInputRef: Ref<HTMLInputElement | undefined>;
  // Per-item progress for the duration of the current batch (replaces the old bare pending count).
  uploadItems: Ref<UploadProgressItem[]>;
  isUploading: Ref<boolean>;
  // Where the current batch is landing, for the progress panel to name. Only meaningful while
  // uploadItems is non-empty; null before the first batch and after the panel is dismissed.
  //
  // Exists because the destination stopped being self-evident: every entry point used to target
  // something visible in the main pane (the pickers target currentPath, a grid row drop targets a
  // row in the current listing), but a drop on the sidebar tree can land in a folder — even a
  // different mount — that is nowhere on screen.
  uploadDestinationPath: Ref<string | null>;
  // Whether files can be uploaded to a SPECIFIC path — path-aware because the virtual root has no
  // drive to write to: it lists the site's two document libraries rather than being a folder in one
  // of them. Mirrors useDirectoryChildrenQuery's "not resolvable" query-disable guard, applied here
  // to the upload UI/entry points instead of a query.
  canUploadTo: (path: string) => boolean;
  // The currentPath case, for FileExplorerHandler's upload buttons' disable.
  canUpload: Ref<boolean>;
  // The path of the single native-file-drop zone the drag is currently over (background zone or a
  // specific folder), or null. Shared so only the innermost hovered zone highlights — mirrors the
  // move drag's `dragOverPath` (useFileManagerDrag). Without this, each zone's isolated isDragOver
  // let the background and the hovered folder light up at the same time.
  nativeDragOverPath: Ref<string | null>;
  // Omit parentPath to target currentPath (the toolbar's own "New > File" convention); pass one
  // explicitly for a call site that means a specific folder — e.g. a folder's own context-menu
  // "Create file" entry — same convention as useFileManagerCreateFolder's openModal.
  openFilePicker: (parentPath?: string) => void;
  openFolderPicker: () => void;
  onFileSelect: (event: Event) => Promise<void>;
  onFolderSelect: (event: Event) => Promise<void>;
  dismissUploadItems: () => void;
  // Shared batch runner so any entry point (picker, native file drop) reports through the same
  // progress panel/isUploading gate. destinationPath is explicit since callers may target a path
  // other than the current folder (e.g. a DropZone breadcrumb/sidebar zone).
  runBatch: (entries: FlattenedUploadEntry[], destinationPath: string) => Promise<void>;
};

export const fileManagerUploadKey: InjectionKey<FileManagerUploadState> =
  Symbol('fileManagerUpload');

// `navigation` and `mounts` are taken as parameters (rather than calling
// `useFileManagerNavigation()`/`useFileManagerMounts()` internally) because this factory is
// provided once at the `FileManager.vue` root, in the same component instance that provides both
// — `inject()` resolves against the *parent* instance's provides, so a component can never inject
// its own `provide()` call. Same reason `provideFileManagerTransfer(navigation, mounts)` already
// takes them explicitly instead of self-injecting them.
export const provideFileManagerUpload = (
  navigation: FileManagerNavigationState,
  mounts: Pick<FileManagerMountsState, 'resolve' | 'hasBackingStore'>,
) => {
  const { currentPath } = navigation;
  const { mutateAsync: uploadFile } = useUploadFileMutation(mounts);
  const { mutateAsync: createFolder } = useCreateDirectoryMutation(mounts);

  const fileInputRef = ref<HTMLInputElement>();
  const folderInputRef = ref<HTMLInputElement>();
  const uploadItems = ref<UploadProgressItem[]>([]);
  const isUploading = ref(false);
  const uploadDestinationPath = ref<string | null>(null);
  const nativeDragOverPath = ref<string | null>(null);
  // Captured at open time (same convention as useFileManagerCreateFolder's pendingParentPath) so
  // onFileSelect targets wherever the picker was opened FOR, not wherever currentPath happens to be
  // once the (async, user-driven) native file dialog actually resolves.
  const pendingUploadPath = ref('');

  // Whether anything backs the path is the whole question here — deliberately NOT isNavigablePath.
  // The virtual root and a namespace level are navigable but have nothing to write into (a namespace
  // is implied by mount prefixes, and SharePoint will not create a document library through this API),
  // so every upload entry point stays shut there.
  const canUploadTo = (path: string) => mounts.hasBackingStore(path);

  const canUpload = computed(() => canUploadTo(currentPath.value));

  // Guards check the TARGET path, not currentPath: the picker may have been opened for a folder the
  // main pane is not currently in.
  const openFilePicker = (parentPath?: string) => {
    const target = parentPath ?? currentPath.value;
    if (!canUploadTo(target)) return;
    pendingUploadPath.value = target;
    fileInputRef.value?.click();
  };

  const openFolderPicker = () => {
    if (canUpload.value) folderInputRef.value?.click();
  };

  const runBatch = async (entries: FlattenedUploadEntry[], destinationPath: string) => {
    if (!entries.length) return;
    // Backstop for every entry point, including any added later — a native drop refuses earlier
    // (before walking the dropped tree), but the context-menu "Create file" picker reaches this one:
    // unlike the toolbar's create button it is not disabled while a batch runs. Notifies rather than
    // returning silently, because a second batch would otherwise overwrite the first one's progress
    // panel and its files would simply never appear.
    if (isUploading.value) {
      notifyUploadBusy();
      return;
    }
    // The last line of defence for every entry point (picker, native OS drop, breadcrumb/sidebar
    // drop zone): a destination with no drive behind it can only produce failures.
    if (!canUploadTo(destinationPath)) return;

    uploadItems.value = entries.map((entry) => ({
      relativePath: entry.relativePath,
      status: 'queued',
    }));
    uploadDestinationPath.value = destinationPath;
    isUploading.value = true;

    const onStatusChange = (relativePath: string, status: UploadItemStatus) => {
      const item = uploadItems.value.find((i) => i.relativePath === relativePath);
      if (item) item.status = status;
    };

    const result = await uploadFolderTree(
      destinationPath,
      entries,
      {
        createFolder: (parentPath, name) => createFolder({ parentPath, name }),
        uploadFile: (parentPath, file) => uploadFile({ parentPath, file }),
      },
      onStatusChange,
    );

    isUploading.value = false;
    notifyUploadBatchResult(result);
  };

  const onFileSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    target.value = '';
    if (!canUploadTo(pendingUploadPath.value)) return;
    await runBatch(
      files.map((file) => ({ relativePath: file.name, file })),
      pendingUploadPath.value,
    );
  };

  const onFolderSelect = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    target.value = '';
    if (!canUploadTo(currentPath.value)) return;
    await runBatch(flattenFileList(files), currentPath.value);
  };

  const dismissUploadItems = () => {
    uploadItems.value = [];
    uploadDestinationPath.value = null;
  };

  const state: FileManagerUploadState = {
    fileInputRef,
    folderInputRef,
    uploadItems,
    isUploading,
    uploadDestinationPath,
    canUploadTo,
    canUpload,
    nativeDragOverPath,
    openFilePicker,
    openFolderPicker,
    onFileSelect,
    onFolderSelect,
    dismissUploadItems,
    runBatch,
  };
  provide(fileManagerUploadKey, state);
  return state;
};

export const useFileManagerUpload = () => {
  const state = inject(fileManagerUploadKey);
  if (!state) {
    throw new Error(
      'useFileManagerUpload() must be called within a component tree provided by provideFileManagerUpload()',
    );
  }
  return state;
};
